import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type ChartDatum = { label: string; value: number; color?: string };

const PALETTE = [
  '#2563eb',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
];

function colorAt(i: number) {
  return PALETTE[i % PALETTE.length];
}

function shortLabel(label: string, max = 12) {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

function displayLabel(value: unknown) {
  const text = String(value ?? '—');
  return /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.slice(0, 10) : text;
}

export const EFormBarChart: React.FC<{ data: ChartDatum[]; height?: number }> = ({
  data,
  height = 220,
}) => {
  const { colors } = useTheme();
  const max = Math.max(1, ...data.map((d) => d.value));
  const step = 112;
  const width = Math.max(420, data.length * step + 48);
  const pad = 28;
  const chartH = height - pad;

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Bar chart">
        {data.map((d, i) => {
          const barW = 36;
          const x = 32 + i * step;
          const h = (d.value / max) * (chartH - 20);
          const y = chartH - h;
          return (
            <g key={`${d.label}-${i}`}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(1, h)}
                rx={4}
                fill={d.color || colorAt(i)}
              />
              <text
                x={x + barW / 2}
                y={Math.max(11, y - 6)}
                textAnchor="middle"
                style={{ fontSize: 10, fontWeight: 600, fill: 'currentColor' }}
              >
                {shortLabel(`${d.label}: ${d.value}`, 18)}
              </text>
              <text
                x={x + barW / 2}
                y={height - 8}
                textAnchor="middle"
                className="fill-current"
                style={{ fontSize: 10, fill: 'currentColor' }}
              >
                {shortLabel(d.label)}
              </text>
              <title>{`${d.label}: ${d.value}`}</title>
            </g>
          );
        })}
        <text x={4} y={14} style={{ fontSize: 10, fill: 'currentColor' }} className={colors.textMuted}>
          {max}
        </text>
      </svg>
    </div>
  );
};

export const EFormLineChart: React.FC<{ data: ChartDatum[]; height?: number }> = ({
  data,
  height = 220,
}) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const step = 112;
  const width = Math.max(420, data.length * step + 48);
  const pad = 28;
  const chartH = height - pad;
  const points = data.map((d, i) => {
    const x = 40 + i * step;
    const y = chartH - (d.value / max) * (chartH - 20);
    return `${x},${y}`;
  });

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Line chart">
        <polyline
          fill="none"
          stroke="#2563eb"
          strokeWidth={2.5}
          points={points.join(' ')}
        />
        {data.map((d, i) => {
          const x = 40 + i * step;
          const y = chartH - (d.value / max) * (chartH - 20);
          return (
            <g key={`${d.label}-${i}`}>
              <circle cx={x} cy={y} r={4} fill="#2563eb" />
              <text
                x={x}
                y={Math.max(11, y - 9)}
                textAnchor="middle"
                style={{ fontSize: 10, fontWeight: 600, fill: 'currentColor' }}
              >
                {shortLabel(`${d.label}: ${d.value}`, 18)}
              </text>
              <text
                x={x}
                y={height - 8}
                textAnchor="middle"
                style={{ fontSize: 10, fill: 'currentColor' }}
              >
                {shortLabel(d.label)}
              </text>
              <title>{`${d.label}: ${d.value}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export const EFormPieChart: React.FC<{ data: ChartDatum[]; size?: number }> = ({
  data,
  size = 200,
}) => {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  let angle = -Math.PI / 2;

  const slices = data.map((d, i) => {
    const portion = (Number(d.value) || 0) / total;
    const start = angle;
    const end = angle + portion * Math.PI * 2;
    angle = end;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const path =
      portion >= 0.999
        ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`
        : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    const mid = start + (end - start) / 2;
    const labelRadius = r * 0.62;
    return {
      path,
      color: d.color || colorAt(i),
      label: d.label,
      value: d.value,
      labelX: cx + labelRadius * Math.cos(mid),
      labelY: cy + labelRadius * Math.sin(mid),
    };
  });

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg width={size} height={size} role="img" aria-label="Pie chart">
        {slices.map((s, i) => (
          <g key={i}>
            <path d={s.path} fill={s.color}>
              <title>{`${s.label}: ${s.value}`}</title>
            </path>
            {Number(s.value) > 0 ? (
              <text
                x={s.labelX}
                y={s.labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  paintOrder: 'stroke',
                  stroke: 'rgba(15, 23, 42, 0.45)',
                  strokeWidth: 2,
                }}
              >
                <tspan x={s.labelX} dy="-0.45em">{shortLabel(s.label, 11)}</tspan>
                <tspan x={s.labelX} dy="1.1em">{s.value}</tspan>
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      <ul className="space-y-1 text-sm">
        {slices.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: s.color }} />
            <span>
              {s.label}: <span className="tabular-nums font-medium">{s.value}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export function buildChartDataFromReport(
  columns: Array<{ key: string; label: string; kind: 'dimension' | 'measure' }>,
  rows: Array<Record<string, unknown>>,
): ChartDatum[] {
  const dim = columns.find((c) => c.kind === 'dimension');
  const measure = columns.find((c) => c.kind === 'measure');
  if (!measure) return [];
  return rows.map((row, i) => ({
    label: dim
      ? displayLabel(row[`${dim.key}__label`] ?? row[dim.key])
      : `Row ${i + 1}`,
    value: Number(row[measure.key] ?? 0) || 0,
  }));
}

export const EFormReportChart: React.FC<{
  chartType: 'table' | 'bar' | 'line' | 'pie';
  columns: Array<{ key: string; label: string; kind: 'dimension' | 'measure' }>;
  rows: Array<Record<string, unknown>>;
}> = ({ chartType, columns, rows }) => {
  const data = useMemo(() => buildChartDataFromReport(columns, rows), [columns, rows]);
  if (chartType === 'table' || !data.length) return null;
  return (
    <div className={cn('rounded-xl border p-4', 'border-slate-200 dark:border-slate-700')}>
      {chartType === 'bar' ? <EFormBarChart data={data} /> : null}
      {chartType === 'line' ? <EFormLineChart data={data} /> : null}
      {chartType === 'pie' ? <EFormPieChart data={data} /> : null}
    </div>
  );
};
